import * as React from 'react';

import { eq } from 'lodash';

import { Button, TemporaryDrawer } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';

import { Navigation, NavigationView } from 'bundles/enterprise-admin-drawers/components/Navigation';
import FilterDrawerButton from 'bundles/enterprise-admin-drawers/components/components/FilterDrawerButton';
import FilterSelector from 'bundles/enterprise-admin-drawers/components/components/FilterSelector';
import FilterSelectorBody from 'bundles/enterprise-admin-drawers/components/components/FilterSelectorBody';

import _t from 'i18n!nls/admin-dashboard';

import 'css!./__styles__/FilterDrawer';

import type { FilterCtrls, FilterOption, FilterType, Filters } from './types';

const { HeadingGroup } = TemporaryDrawer;

export type Props = {
  open: boolean;
  onClose: () => void;
  onChange: (filters: Filters) => void;
  showSelectAllOption?: boolean;
  filters: Filters;
  filterCtrls: FilterCtrls;
};

export type State = {
  localFilters: Filters;
  tempLocalFilters: Filters;
  navIndex: number;
  currentFilterKey: keyof Filters | null;
};

export class FilterDrawer extends React.Component<Props, State> {
  selectedFilterRef: HTMLDivElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      localFilters: props.filters,
      tempLocalFilters: props.filters,
      navIndex: 0,
      currentFilterKey: null,
    };
  }

  componentDidMount() {
    this.resetLocalFilters();
  }

  componentWillReceiveProps(nextProps: Props) {
    const { filterCtrls, filters } = this.props;

    // clear filters if its control has been removed as an option
    if (!eq(nextProps.filterCtrls, filterCtrls)) {
      this.setState({ currentFilterKey: null });
      Object.keys(filters).forEach((filterKey) => {
        if (!filterCtrls[filterKey]) {
          this.handleLocalFilterChange(filterKey)([]);
        }
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { open } = this.props;

    // If the drawer is just opened, reset localFilters
    if (open && !prevProps.open) {
      this.resetLocalFilters();
    }
  }

  handleCancel = () => {
    const { onClose } = this.props;
    this.resetLocalFilters();
    onClose();
  };

  /**
   * Clear all selected filter options under given type
   */
  handleClear = (filterType: FilterType) => () => {
    this.handleFilterChange(filterType)([]);
  };

  handleLocalClear = (key: string | number) => () => {
    this.handleLocalFilterChange(key)([]);
  };

  handleClearAll = () => {
    const { filters } = this.props;
    this.setState({ localFilters: filters, tempLocalFilters: filters });
  };

  handleReset = () => {
    this.resetLocalFilters();
  };

  handleSave = () => {
    const { onClose, onChange } = this.props;
    const { localFilters } = this.state;

    this.returnToRootLevel();
    onChange(localFilters);
    onClose();
  };

  handleOpenFilterSelector = (key: string) => () => {
    this.setState({ navIndex: 1, currentFilterKey: key });
  };

  handleNavBack = () => {
    this.selectedFilterRef?.focus();

    this.setState((prevState) => ({
      navIndex: prevState.navIndex - 1,
      tempLocalFilters: { ...prevState.localFilters },
      currentFilterKey: null,
    }));
  };

  handleUpdate = () => {
    this.setState((prevState) => ({ localFilters: { ...prevState.tempLocalFilters } }));
    this.handleNavBack();
  };

  handleFilterChange = (key: string | number) => (filterOptions: FilterOption[]) => {
    this.setState((prevState: State) => ({
      localFilters: {
        ...prevState.localFilters,
        [key]: filterOptions,
      },
    }));

    this.handleLocalFilterChange(key)(filterOptions);
  };

  handleLocalFilterChange = (key: string | number) => (filterOptions: FilterOption[]) => {
    this.setState((prevState: State) => ({
      tempLocalFilters: {
        ...prevState.localFilters,
        [key]: filterOptions,
      },
    }));
  };

  resetLocalFilters() {
    const { filters } = this.props;
    this.setState({ localFilters: filters, tempLocalFilters: filters });
    this.returnToRootLevel();
  }

  returnToRootLevel() {
    this.setState({ navIndex: 0 });
  }

  isApplyDisabled() {
    const { filters } = this.props;
    const { localFilters } = this.state;
    return eq(filters, localFilters);
  }

  isUpdateDisabled() {
    const { tempLocalFilters, localFilters } = this.state;
    return eq(localFilters, tempLocalFilters);
  }

  isClearAllDisabled() {
    const { localFilters, currentFilterKey } = this.state;
    if (currentFilterKey) {
      return localFilters[currentFilterKey].length !== 0;
    } else {
      return false;
    }
  }

  isDeselectAllDisabled() {
    const { tempLocalFilters, currentFilterKey } = this.state;
    if (currentFilterKey) {
      return tempLocalFilters[currentFilterKey].length === 0;
    } else {
      return true;
    }
  }

  render() {
    const { open, showSelectAllOption, filterCtrls } = this.props;
    const { navIndex, currentFilterKey, localFilters, tempLocalFilters } = this.state;

    return (
      <TemporaryDrawer open={open} anchor="right" className="rc-FilterDrawer" onClose={this.handleCancel}>
        <HeadingGroup>{_t('Filters')}</HeadingGroup>

        <div className="vertical-box h-100">
          <Navigation index={navIndex} className="h-100">
            <NavigationView className="vertical-box">
              <div className="flex-1 h-100 c-NavigationViewList">
                {Object.keys(filterCtrls).map((key) => (
                  <FilterSelector
                    data-testid={`${filterCtrls[key].type}-filter`}
                    key={key}
                    filterCtrl={filterCtrls[key]}
                    selectedFilterOptions={localFilters[key]}
                    onOpen={this.handleOpenFilterSelector(key)}
                    onChange={this.handleFilterChange(key)}
                    selectedRef={
                      key === currentFilterKey
                        ? (ref: HTMLDivElement) => {
                            this.selectedFilterRef = ref;
                            return ref;
                          }
                        : undefined
                    }
                  />
                ))}
              </div>

              <div className="border-top p-a-2 horizontal-box w-100 align-items-left">
                <FilterDrawerButton
                  onClick={this.handleSave}
                  isDisabled={this.isApplyDisabled()}
                  label={_t('Apply')}
                  type="update"
                />
                <FilterDrawerButton
                  onClick={this.handleClearAll}
                  isDisabled={this.isClearAllDisabled()}
                  label={_t('Clear all')}
                  type="clearAll"
                />
              </div>
            </NavigationView>

            {currentFilterKey && typeof tempLocalFilters[currentFilterKey] !== 'undefined' ? (
              <NavigationView className="vertical-box">
                <div className="align-items-left m-l-1">
                  <Button
                    variant="ghost"
                    size="small"
                    icon={<ArrowPreviousIcon />}
                    iconPosition="before"
                    onClick={this.handleNavBack}
                  >
                    {_t('Back')}
                  </Button>
                </div>

                <FilterSelectorBody
                  className="flex-1 mh-0"
                  showSelectAllOption={showSelectAllOption}
                  filterCtrl={filterCtrls[currentFilterKey]}
                  selectedFilterOptions={tempLocalFilters[currentFilterKey]}
                  onChange={this.handleLocalFilterChange(currentFilterKey)}
                />

                <div className="border-top p-a-2 horizontal-box w-100 align-items-left">
                  <FilterDrawerButton
                    isDisabled={this.isUpdateDisabled()}
                    onClick={this.handleUpdate}
                    label={_t('Update')}
                    type="update"
                  />

                  <FilterDrawerButton
                    onClick={this.handleLocalClear(currentFilterKey)}
                    isDisabled={this.isDeselectAllDisabled()}
                    label={_t('Deselect all')}
                    type="clearAll"
                  />
                </div>
              </NavigationView>
            ) : (
              <NavigationView className="vertical-box" />
            )}
          </Navigation>
        </div>
      </TemporaryDrawer>
    );
  }
}

export default FilterDrawer;
